(ns frontend.view.grid.transaction-processes-grid.events
  (:require
   [common.localization :as loc :refer [tr]]
   [common.roles :as roles]
   [common.schema.registry :as registry]
   [common.transaction-process.access :as tp-access]
   [common.transaction-process.templates.registry :as template-registry]
   [common.util :as cu]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.conn :as conn]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.grid.transaction-processes-grid.columns :as columns]
   [frontend.view.grid.transaction-processes-grid.db :as db]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [promesa.core :as p]
   [reagent.core :as r]))

(declare setup-args resolve-column-keys)

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :transaction-processes-view-settings]
   rest))

(defn- selectable-column?
  "A TP schema field is offered as a selectable grid column when it has a
   renderable grid editor type, isn't a hidden/system field, and the user's role
   can read it. TP field enablement is template-driven (per process), so we
   deliberately do NOT gate the column universe on the schema's in-use default —
   any role-readable field can back a custom view (blank where a process doesn't
   use it), matching the breadth of the other entity grids' column pickers."
  [role [k fs]]
  (let [grid-vis (-> fs :field/default-visibility :grid)]
    (and grid-vis
         (not= grid-vis :editor.type/none)
         (not (:field/hidden-from-configuration? fs))
         (not (:field/hidden-from-config? fs))
         (not (:field/secondary-field? fs))               ;; drop currency-symbol companions (--sym)
         (not= :data.type/currency-symbol (:field/data-type fs))
         (not= "common" (namespace k))                    ;; internal :common/* (created-on injected separately)
         (not= :transaction-process/lux-fund? k)          ;; system snapshot, not a user column
         (roles/read-access? (roles/resolve-role-field-access
                              role (:field/default-authorization fs))))))

(defn precalculated-schema
  "[key field-schema] pairs for every selectable TP column, sorted by label with
   the short-name first. Cached on db/schema (recomputed on permission changes via
   the permission-updates tracker) since column-keys is hit on every grid render."
  [recalculate?]
  (let [cached        @db/schema
        tenant-schema @session/tenant-schema
        role          @session/role]
    (if (and (not recalculate?) cached)
      cached
      (let [schema (->> (registry/get-schema tenant-schema :entity.type/transaction-process)
                        (filter (partial selectable-column? role))
                        (sort (fn [[a _] [b _]]
                                (cond
                                  (= :transaction-process/short-name a) -1
                                  (= :transaction-process/short-name b) 1
                                  :else
                                  (compare (loc/field-name a (registry/get-field-schema tenant-schema a))
                                           (loc/field-name b (registry/get-field-schema tenant-schema b))))))
                        doall)]
        (reset! db/schema schema)
        schema))))

(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn column-keys
  "Selectable column universe: schema-derived TP fields plus :common/created-on
   and :transaction-process/template-kw (both hidden-from-config / grid-:none, so
   excluded from the schema set and injected explicitly the same way :db/id is)
   and, for admins, :db/id."
  []
  (->> (concat (when (session/is-admin?) [:db/id])
               [:common/created-on :transaction-process/template-kw]
               (map first (keys-and-schemas)))
       (distinct)))

(defn visible-keys-and-schemas
  "Key+schema pairs for the currently visible columns. Used by the engine's
   universal search to know what to match against."
  []
  (let [tenant-schema @session/tenant-schema]
    (->> (resolve-column-keys)
         (keep (fn [k]
                 (when-let [fs (registry/get-field-schema tenant-schema k)]
                   [k fs]))))))

(defn setup-args
  "Config map handed to the shared scrollable-grid engine fns. No kanban."
  []
  {:state                       db/transaction-processes
   :entity-type                 :entity.type/transaction-process
   :modal-state                 db/modal-state
   :selected-columns-state      db/selected-columns
   :ordered-columns-state       db/ordered-columns
   :filters-state               db/filters
   :view-settings-state         db/view-settings
   :column-widths-state         db/column-widths
   :selected-pipeline           nil
   :ls-path                     ls-path
   :column-keys                 column-keys
   :default-columns             columns/default-columns
   :mandatory?                  columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn stage-label
  "Human label for a TP stage, resolved from its template's :template/stages
   (the schema keeps stage options empty — labels are template-driven). Falls
   back to the bare keyword name when the template has no matching stage."
  [template stage]
  (when stage
    (or (->> (:template/stages template)
             (some (fn [s] (when (= (:stage/id s) stage) (:stage/label s)))))
        (name stage))))

(defn keyword-label-from-template
  "Label for a template-driven keyword field (e.g. transaction-type) whose
   schema options are empty — options live on the template's :template/fields.
   Falls back to the bare keyword name when no matching option exists."
  [{:keys [template field-kw value]}]
  (when value
    (or (->> (get-in template [:template/fields field-kw :field/options])
             (some (fn [opt] (when (= (:value opt) value) (:label opt)))))
        (name value))))

(defn- enrich-row
  "Pre-compute the display strings the schema-driven engine can't resolve
   (stage / transaction-type labels and the template name all come from the
   process's template, not its field schema) and stash them under :display-data,
   which the grid cell renderer prefers over recomputing."
  [tp]
  (let [template (some-> (:transaction-process/template-kw tp)
                         template-registry/get-template)
        display  (cu/remove-nil-vals
                  {:transaction-process/stage
                   (stage-label template (:transaction-process/stage tp))
                   :transaction-process/transaction-type
                   (keyword-label-from-template
                    {:template template
                     :field-kw :transaction-process/transaction-type
                     :value    (:transaction-process/transaction-type tp)})
                   :transaction-process/template-kw
                   (:template/name template)})]
    (assoc tp :display-data display)))

(defn- accessible?
  "Row-level access using the same policy as backend TP subscriptions."
  [ctx tp]
  (tp-access/can-access-transaction-process?
   (assoc ctx
          :entity (update tp :transaction-process/vehicle session/get-entity))))

(defn active-grid-process?
  "True for a status-bearing, non-soft-deleted process. `boolean` because the
  bare `and` returns nil for a status-less entity, and a `?` predicate should
  answer with a boolean."
  [tp]
  (boolean
   (and (:transaction-process/status tp)
        (not (:common/disabled? tp)))))

(defn process-updates
  "Mirror the accessible, status-bearing transaction processes from the session
   into the grid state, each enriched with its template-derived display strings."
  []
  (let [ctx  {:tenant                      @session/tenant
              :tenant-plan                 @session/tenant-plan
              :role                        @session/role
              :account                     @session/account
              :account-current-permissions @session/current-permissions}
        rows (->> (session/get-entities :entity.type/transaction-process)
                  (filter active-grid-process?)
                  (filter (partial accessible? ctx))
                  (mapv enrich-row))]
    (swap! db/transaction-processes assoc :grid-entities rows)))

(defn permission-updates
  "Recompute the cached selectable-column schema when the user's permissions
   change (so column access stays correct without a remount)."
  []
  (when @session/current-permissions
    (precalculated-schema true)))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger    @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/transaction-processes assoc :loaded true)
                    (deref db/transaction-processes))))))

(defn system-views
  "Built-in, non-deletable named views. The first entry is the default selected
   on first load: \"Active transaction processes\" hides completed ones; \"All\"
   shows every status."
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :common/created-on
                          :sort-direction   "DESC"
                          :filters          {:transaction-process/status
                                             {:operand           :operand/any-of
                                              :lower-bound-value #{:transaction-process.status/active}}}}
    :named-view/name     "Active transaction processes"
    :disable-delete?     true
    :db/id               "named-view-active-transaction-processes"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :common/created-on
                          :sort-direction   "DESC"}
    :named-view/name     "All transaction processes"
    :disable-delete?     true
    :db/id               "named-view-all-transaction-processes"}])

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :transaction-processes-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (session/get-entity id)
      (->> (system-views)
           (filter (fn [v] (= (:db/id v) id)))
           first)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views) first :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view]
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn save-current-view!
  [name]
  (named-view-actions/*create-named-view->
   {:named-view/name     name
    :named-view/view     :transaction-processes-grid
    :named-view/settings @db/view-settings}
   identity
   (fn [& a]
     (logging/error "create-named-view failed" a))))

(defn toggle-quick-filters!
  []
  (swap! db/transaction-processes update :show-quick-filters? not))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/transaction-processes assoc
           :tracker-1 (r/track! process-updates)
           :tracker-2 (r/track! permission-updates)
           :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/transaction-processes)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/transaction-processes)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/transaction-processes)]
    (r/dispose! tracker))
  (swap! db/transaction-processes dissoc
         :loaded
         :schema
         :view-settings
         :sorted-grid-entities
         :search
         :selected
         :grid-entities))

(defn mass-delete!
  []
  (-> (conn/sendp! :transaction-process/mass-delete
                   {:transaction-process-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_resp]
                (swap! db/transaction-processes assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected transaction processes" e)))))

(defn delete-selected-clicked
  [_]
  (modals/open-modal!
   confirmation/modal-key
   {:body          [:div
                    [:p (tr :confirm-delete-selected)]]
    :confirm-text  (tr :confirm-delete-selected-button)
    :checkbox-text (tr :confirm-delete-selected-checkbox)
    :on-confirm    mass-delete!}))
